import classNames from "classnames";
import { useCallback, useId, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import Icon from "~/components/shared/Icon";

interface Props {
  onChange?: (value: string) => void;
}

export const TableSearchInput = ({ onChange }: Props) => {
  const id = useId();
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const clearValue = useCallback(() => {
    setValue("");
    onChange?.("");
    inputRef.current?.focus();
  }, [onChange]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    onChange?.(newValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.currentTarget.blur();
    }
  };

  return (
    <div
      className={twMerge(
        classNames(
          "group/input relative flex max-w-64 items-center rounded-md ring-purple-600 transition-shadow focus-within:ring-2 hover:[&:not(:focus-within)]:ring-1 hover:[&:not(:focus-within)]:ring-slate-400",
          {
            "[&:not(:focus-within)]:ring-1 [&:not(:focus-within)]:ring-slate-400": value !== "",
          },
        ),
      )}
    >
      <label htmlFor={id} className="sr-only">
        Search jobs
      </label>

      <input
        ref={inputRef}
        id={id}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Search"
        data-testid="table-search"
        className="input placeholder:text-charcoal-700 focus:placeholder:text-charcoal-300 text-charcoal-700 peer h-7 w-48 appearance-none rounded-md border-0 bg-transparent px-2 pl-7 pr-7 text-sm shadow-none outline-none transition-colors focus:bg-white group-hover/input:bg-white"
      />
      <Icon
        icon="heroicons/16/solid/magnifying-glass"
        className="text-charcoal-700 peer-focus:text-charcoal-300 peer-[:not(:placeholder-shown)]:text-charcoal-300 pointer-events-none absolute left-2 h-4 w-4"
        aria-hidden="true"
      />
      <button className="absolute right-2" onClick={clearValue}>
        <Icon
          icon="heroicons/20/solid/x-circle"
          className={twMerge(
            classNames("text-charcoal-300 hover:text-charcoal-700 invisible h-4 w-4", {
              visible: value !== "",
            }),
          )}
        />
      </button>
    </div>
  );
};
